'use client';
import { useCallback, useEffect, useState } from 'react';
const STORAGE_KEY = 'isSpeakerMuted';
const EVENT_NAME = 'global-silence-changed';
export const useGlobalSilence = (initialState = true) => {
    const [isSpeakerMuted, setIsSpeakerMutedInternal] = useState(() => {
        if (typeof window !== 'undefined') {
            const saved = sessionStorage.getItem(STORAGE_KEY);
            return saved !== null ? saved === 'true' : initialState;
        }
        return initialState;
    });
    const getAllMediaElements = useCallback(() => {
        const media = [];
        const findMedia = (root) => {
            if (root instanceof HTMLElement || root instanceof ShadowRoot) {
                const found = root.querySelectorAll('video, audio');
                found.forEach(el => media.push(el));
            }
            if ('childNodes' in root) {
                root.childNodes.forEach(child => {
                    if (child instanceof HTMLElement && child.shadowRoot) {
                        findMedia(child.shadowRoot);
                    }
                    findMedia(child);
                });
            }
        };
        // 1. Prioritize searching inside our own shadow root wrapper
        const wrapper = document.getElementById('lk-embed-wrapper');
        if (wrapper) {
            findMedia(wrapper);
            if (wrapper.shadowRoot)
                findMedia(wrapper.shadowRoot);
        }
        else {
            // 2. Fallback to whole body (necessary for non-shadow-dom or standalone usage)
            findMedia(document.body);
        }
        return media;
    }, []);
    const applySilence = useCallback((mute) => {
        if (typeof window === 'undefined')
            return;
        const media = getAllMediaElements();
        media.forEach((el) => {
            if (el.muted !== mute) {
                el.muted = mute;
            }
            const targetVolume = mute ? 0 : 1;
            if (el.volume !== targetVolume) {
                el.volume = targetVolume;
            }
        });
    }, [getAllMediaElements]);
    const setIsSpeakerMuted = useCallback((mute) => {
        setIsSpeakerMutedInternal(mute);
        if (typeof window !== 'undefined') {
            sessionStorage.setItem(STORAGE_KEY, String(mute));
            window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: mute }));
        }
    }, []);
    useEffect(() => {
        const handleSync = (e) => {
            setIsSpeakerMutedInternal(e.detail);
        };
        window.addEventListener(EVENT_NAME, handleSync);
        return () => window.removeEventListener(EVENT_NAME, handleSync);
    }, []);
    useEffect(() => {
        applySilence(isSpeakerMuted);
        const observer = new MutationObserver(() => {
            applySilence(isSpeakerMuted);
        });
        const observerOptions = {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['muted', 'volume'],
        };
        observer.observe(document.body, observerOptions);
        // Also observe the shadow root if it exists
        const wrapper = document.getElementById('lk-embed-wrapper');
        if (wrapper && wrapper.shadowRoot) {
            observer.observe(wrapper.shadowRoot, observerOptions);
        }
        return () => observer.disconnect();
    }, [isSpeakerMuted, applySilence]);
    return { isSpeakerMuted, setIsSpeakerMuted };
};
